import { useState, type ReactNode } from 'react'
import { dirOf, fmtPct, fmtUsd } from '../format'
import type { RecType } from '../api'

// Ported from the design's app_primitives.jsx.

export type IconName =
  | 'home' | 'rec' | 'stock' | 'podcast' | 'episode' | 'search' | 'play' | 'pause'
  | 'chevron' | 'arrow-up' | 'arrow-dn' | 'dot' | 'star' | 'close' | 'sun' | 'moon' | 'auto'

export function Icon({ name, size = 16 }: { name: IconName; size?: number }) {
  const common = {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
    strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true,
  }
  switch (name) {
    case 'home':     return <svg {...common}><path d="M3 12l9-8 9 8"/><path d="M5 10v10h14V10"/></svg>
    case 'rec':      return <svg {...common}><path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/></svg>
    case 'stock':    return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
    case 'podcast':  return <svg {...common}><rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0 0 14 0"/><path d="M12 18v3"/></svg>
    case 'episode':  return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none"/></svg>
    case 'search':   return <svg {...common}><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
    case 'play':     return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M8 5v14l11-7z"/></svg>
    case 'pause':    return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>
    case 'chevron':  return <svg {...common}><path d="M9 6l6 6-6 6"/></svg>
    case 'arrow-up': return <svg {...common}><path d="M7 17L17 7"/><path d="M8 7h9v9"/></svg>
    case 'arrow-dn': return <svg {...common}><path d="M7 7l10 10"/><path d="M17 8v9h-9"/></svg>
    case 'dot':      return <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden><circle cx="12" cy="12" r="4" fill="currentColor"/></svg>
    case 'star':     return <svg {...common}><path d="M12 3l2.5 6 6.5.5-5 4.5 1.5 6.5L12 17l-5.5 3.5L8 14 3 9.5l6.5-.5z"/></svg>
    case 'close':    return <svg {...common}><path d="M6 6l12 12M18 6L6 18"/></svg>
    case 'sun':      return <svg {...common}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>
    case 'moon':     return <svg {...common}><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z"/></svg>
    case 'auto':     return <svg {...common}><circle cx="12" cy="12" r="8"/><path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" stroke="none"/></svg>
  }
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z0-9]/.test(w) && !/^(the|and|with|of|a)$/i.test(w))
    .slice(0, 3)
    .map((w) => w[0].toUpperCase())
    .join('')
}

// Podcast artwork; initials when a feed has none or its host refuses to serve
// it cross-origin (Financial Sense sends Cross-Origin-Resource-Policy: same-origin).
export function PodAv({ name, image, size = 'sq' }: { name: string; image: string | null; size?: string }) {
  const [failed, setFailed] = useState(false)
  return (
    <div className={`av ${size}`} title={name}>
      {image && !failed ? <img src={image} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} /> : initials(name)}
    </div>
  )
}

export function DirPill({ type }: { type: RecType | null }) {
  const dir = dirOf(type)
  if (!dir) return null
  const cls = dir === 'BUY' ? 'up' : dir === 'SELL' ? 'down' : 'hold'
  return <span className={`pill ${cls}`}>{dir}</span>
}

export function Conv({ n }: { n: number | null }) {
  if (n == null) return null
  return (
    <span className="conv" title={`Conviction ${n}/5`} aria-label={`Conviction ${n} of 5`}>
      {[1, 2, 3, 4, 5].map((i) => <span key={i} className={i <= n ? 'on' : ''} />)}
    </span>
  )
}

// A return shown as % or $ per share; body[data-metric] picks which.
export function MetricText({ pct, usd, className = '' }: { pct: number | null; usd: number | null; className?: string }) {
  if (pct == null) return <span className={`t-num ${className}`}>—</span>
  return (
    <span className={`t-num ${pct >= 0 ? 'text-up' : 'text-down'} ${className}`}>
      <span className="metric-view pct">{fmtPct(pct)}</span>
      <span className="metric-view usd">{fmtUsd(usd)}</span>
    </span>
  )
}

export function MetricSwitch({ metric, onChange }: { metric: 'pct' | 'usd'; onChange: (m: 'pct' | 'usd') => void }) {
  return (
    <div className="seg" role="group" aria-label="Show returns as">
      <button className={metric === 'pct' ? 'active' : ''} onClick={() => onChange('pct')} aria-pressed={metric === 'pct'}>%</button>
      <button className={metric === 'usd' ? 'active' : ''} onClick={() => onChange('usd')} aria-pressed={metric === 'usd'}>$</button>
    </div>
  )
}

export function Sparkline({ values, w = 80, h = 28 }: { values: number[] | null; w?: number; h?: number }) {
  if (!values || values.length < 2) return <svg width={w} height={h} aria-hidden />
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const y = (v: number) => 2 + (1 - (v - min) / span) * (h - 4)
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${((i / (values.length - 1)) * w).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  const up = values[values.length - 1] >= values[0]
  const color = up ? 'var(--up)' : 'var(--down)'
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <path d={`${d} L${w},${h} L0,${h} Z`} fill={color} opacity="0.09" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ---- Loading / error / empty states (not in the design) ----

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return <div className="state" role="status">{label}</div>
}

export function ErrorState({ error, retry }: { error: Error; retry?: () => void }) {
  return (
    <div className="state error" role="alert">
      <p>Couldn’t load this page.</p>
      <p className="t-sm">{error.message}</p>
      {retry && <button className="btn" onClick={retry}>Try again</button>}
    </div>
  )
}

export function NotFound({ what, children }: { what: string; children?: ReactNode }) {
  return (
    <div className="state" data-testid="not-found">
      <p className="serif" style={{ fontSize: 22, color: 'var(--ink)', margin: '0 0 6px' }}>{what} not found</p>
      {children}
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="state">{children}</div>
}
